import React, { createContext, useContext, useState, useEffect } from 'react';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const [metaData, setMetaData] = useState(null);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [frontendPermissions, setFrontendPermissions] = useState([]);
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load data from localStorage on initialization
  useEffect(() => {
    try {
      const storedMetaData = localStorage.getItem('appMetaData');
      const storedUser = localStorage.getItem('appUser');
      const storedRoles = localStorage.getItem('appRoles');
      const storedPermissions = localStorage.getItem('appPermissions');
      const storedFrontendPermissions = localStorage.getItem('appFrontendPermissions');

      if (storedMetaData) {
        setMetaData(JSON.parse(storedMetaData));
      }
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      if (storedRoles) {
        setRoles(JSON.parse(storedRoles));
      }
      if (storedPermissions) {
        setPermissions(JSON.parse(storedPermissions));
      }
      if (storedFrontendPermissions) {
        setFrontendPermissions(JSON.parse(storedFrontendPermissions));
      }
      // Mark permissions as ready if loaded from localStorage
      if (storedPermissions || storedFrontendPermissions) {
        setPermissionsReady(true);
      }
    } catch (error) {
      console.error('Error loading app data from localStorage:', error);
      clearAppData();
    }
  }, []);

  // Function to update app data
  const updateAppData = (data) => {
    try {
      if (data.metaData) {
        setMetaData(data.metaData);
        localStorage.setItem('appMetaData', JSON.stringify(data.metaData));
      }
      if (data.user) {
        setUser(data.user);
        localStorage.setItem('appUser', JSON.stringify(data.user));
      }
      if (data.roles) {
        setRoles(data.roles);
        localStorage.setItem('appRoles', JSON.stringify(data.roles));
      }
      if (data.permissions) {
        setPermissions(data.permissions);
        localStorage.setItem('appPermissions', JSON.stringify(data.permissions));
      }
      if (data.frontendPermissions !== undefined) {
        setFrontendPermissions(data.frontendPermissions);
        localStorage.setItem('appFrontendPermissions', JSON.stringify(data.frontendPermissions));
      }
      // Mark permissions as ready after updating
      if (data.permissions || data.frontendPermissions !== undefined) {
        setPermissionsReady(true);
      }
    } catch (error) {
      console.error('Error updating app data:', error);
    }
  };

  // Function to clear app data
  const clearAppData = () => {
    setMetaData(null);
    setUser(null);
    setRoles([]);
    setPermissions([]);
    setFrontendPermissions([]);
    setPermissionsReady(false);
    localStorage.removeItem('appMetaData');
    localStorage.removeItem('appUser');
    localStorage.removeItem('appRoles');
    localStorage.removeItem('appPermissions');
    localStorage.removeItem('appFrontendPermissions');
  };

  // Function to get store list from metaData
  const getStoreList = () => {
    return metaData?.storeList || [];
  };

  // Function to get store by ID
  const getStoreById = (storeId) => {
    const storeList = getStoreList();
    return storeList.find(store => store.id === storeId);
  };

  // Function to get store by name
  const getStoreByName = (storeName) => {
    const storeList = getStoreList();
    return storeList.find(store => store.name === storeName);
  };

  // Function to check if user has specific role
  const hasRole = (role) => {
    return roles.includes(role);
  };

  // Function to check if user has any of the specified roles
  const hasAnyRole = (roleList) => {
    return roleList.some(role => roles.includes(role));
  };

  // Function to check if user is admin
  const isAdmin = () => {
    return hasRole('admin');
  };

  // Function to get device list from metaData
  const getDeviceList = () => {
    return metaData?.devices || [];
  };

  // Function to get device by ID
  const getDeviceById = (deviceId) => {
    const deviceList = getDeviceList();
    return deviceList.find(device => device.id === deviceId);
  };

  // Function to get selected devices from localStorage
  const getSelectedDevices = () => {
    try {
      const storedDevices = localStorage.getItem('selectedDevices');
      return storedDevices ? JSON.parse(storedDevices) : [];
    } catch (error) {
      console.error('Error loading selected devices from localStorage:', error);
      return [];
    }
  };

  // Function to save selected devices to localStorage
  const saveSelectedDevices = (deviceIds) => {
    try {
      localStorage.setItem('selectedDevices', JSON.stringify(deviceIds));
      return true;
    } catch (error) {
      console.error('Error saving selected devices to localStorage:', error);
      return false;
    }
  };

  // Function to get selected device objects
  const getSelectedDeviceObjects = () => {
    const selectedIds = getSelectedDevices();
    const deviceList = getDeviceList();
    return deviceList.filter(device => selectedIds.includes(device.id));
  };

  // Permission checking functions
  const hasPermission = (permission) => {
    // Admin has all permissions
    if (permissions.includes('admin')) {
      return true;
    }
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    // Admin has all permissions
    if (permissions.includes('admin')) {
      return true;
    }
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    // Admin has all permissions
    if (permissions.includes('admin')) {
      return true;
    }
    return permissionList.every(permission => permissions.includes(permission));
  };

  // Frontend permission checking functions
  const hasFrontendPermission = (permission) => {
    // Admin has all permissions
    if (permissions.includes('admin')) {
      return true;
    }
    return frontendPermissions.includes(permission);
  };

  const hasAnyFrontendPermission = (permissionList) => {
    // Admin has all permissions
    if (permissions.includes('admin')) {
      return true;
    }
    return permissionList.some(permission => frontendPermissions.includes(permission));
  };

  const hasAllFrontendPermissions = (permissionList) => {
    // Admin has all permissions
    if (permissions.includes('admin')) {
      return true;
    }
    return permissionList.every(permission => frontendPermissions.includes(permission));
  };

  // Function to check if a menu should be visible
  const isMenuVisible = (menuPermission) => {
    // Admin sees all menus
    if (permissions.includes('admin')) {
      return true;
    }
    return frontendPermissions.includes(menuPermission);
  };

  const value = {
    metaData,
    user,
    roles,
    permissions,
    frontendPermissions,
    permissionsReady,
    loading,
    setLoading,
    updateAppData,
    clearAppData,
    getStoreList,
    getStoreById,
    getStoreByName,
    hasRole,
    hasAnyRole,
    isAdmin,
    getDeviceList,
    getDeviceById,
    getSelectedDevices,
    saveSelectedDevices,
    getSelectedDeviceObjects,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasFrontendPermission,
    hasAnyFrontendPermission,
    hasAllFrontendPermissions,
    isMenuVisible,
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};